import { Button, Empty, Input, Modal } from "antd";
import React, { useCallback, useMemo, useState } from "react";
import { useGlobalFilter, useSortBy, useTable } from "react-table";
import { ToastContainer } from "react-toastify";
import EmployeeImporter from "./EmployeeImporter";
import ExportEmployees from "./ExportEmployees";
import RegisterEmployee from "./RegisterEmployee";

const GlobalFilter = ({ globalFilter, setGlobalFilter }) => {
  return (
    <Input
      placeholder="Buscar empleados..."
      value={globalFilter || ""}
      onChange={(e) => setGlobalFilter(e.target.value || undefined)}
      style={{ marginBottom: "0px", width: "50%", minWidth: "250px" }}
    />
  );
};

/**
 * Employee list table for union staff (superadmin / admin_empleados).
 * @param {Array} data
 * @param {Function} setEmployeeList
 * @param {Function} refreshEmployeeList
 * @param {boolean} canRegister - When true, show manual register (superadmin only).
 */
const AllEmployeeTable = ({
  data,
  setEmployeeList,
  refreshEmployeeList,
  canRegister = false,
}) => {
  const [visibleRows, setVisibleRows] = useState(25);
  const [isImporterOpen, setIsImporterOpen] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);

  const handleImportSuccess = () => {
    setIsImporterOpen(false);
    if (typeof refreshEmployeeList === "function") {
      refreshEmployeeList();
    }
  };

  const loadMoreRows = useCallback(() => {
    setVisibleRows((prev) => prev + 25);
  }, []);

  const columns = useMemo(
    () => [
      { Header: "N° afiliado", accessor: "numero_afiliado" },
      { Header: "Apellido y Nombre", accessor: "name" },
      { Header: "CUIL", accessor: "cuil" },
      { Header: "Email", accessor: "email" },
      {
        Header: "Estado",
        accessor: "status",
        Cell: ({ value, row }) => {
          if (value && value.toLowerCase() === "baja") {
            const motivo = row.original.reason || "Sin motivo";
            return (
              <span title={`Motivo: ${motivo}`}>
                Baja
              </span>
            );
          }
          return "Activo";
        },
      },
      {
        Header: "Tipo Aporte",
        accessor: "tipo_aporte",
        Cell: ({ value }) => (!value || value == "" ? "Sindical" : value),
      },
      {
        Header: "Ingreso Bruto",
        accessor: "salary",
        Cell: ({ value }) => `$${value}`,
      },
      { Header: "Compania", accessor: "company.name" },
    ],
    []
  );

  const tableInstance = useTable(
    { columns, data, initialState: { pageSize: 25 } },
    useGlobalFilter,
    useSortBy
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    setGlobalFilter,
  } = tableInstance;

  const { globalFilter } = state;

  const filteredRows = useMemo(
    () => rows.slice(0, visibleRows),
    [rows, visibleRows]
  );

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop < clientHeight + 10) {
      loadMoreRows();
    }
  };

  const toolbar = (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "10px",
        flexWrap: "wrap",
        backgroundColor: "#fff",
        padding: "10px",
        position: data.length === 0 ? "static" : "sticky",
        top: 0,
        zIndex: 1,
      }}
    >
      {data.length > 0 ? (
        <GlobalFilter
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
        />
      ) : (
        <div />
      )}
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
        {canRegister && (
          <Button type="primary" onClick={() => setOpenDrawer(true)}>
            + Nuevo
          </Button>
        )}
        <ExportEmployees employees={data} />
        <Button onClick={() => setIsImporterOpen(true)} type="primary">
          Importar Empleados
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <ToastContainer />
      {data.length === 0 ? (
        <>
          {toolbar}
          <Empty
            style={{ marginTop: "10%" }}
            description="No hay empleados cargados."
          />
        </>
      ) : (
        <div
          className="card-body"
          onScroll={handleScroll}
          style={{ height: "700px", overflowY: "auto", padding: "0px" }}
        >
          <div className="dataTables_wrapper">
            {toolbar}
            <table {...getTableProps()} className="table dataTable display">
              <thead
                style={{
                  position: "sticky",
                  top: "50px",
                  backgroundColor: "#fff",
                  zIndex: 1,
                }}
              >
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                      >
                        {column.render("Header")}
                        {column.canSort && (
                          <span className="ml-1">
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <i className="fa fa-arrow-down ms-2 fs-14" />
                              ) : (
                                <i className="fa fa-arrow-up ms-2 fs-14" />
                              )
                            ) : (
                              <i className="fa fa-sort ms-2 fs-14" />
                            )}
                          </span>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {filteredRows.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {canRegister && (
        <RegisterEmployee
          openDrawer={openDrawer}
          setOpenDrawer={setOpenDrawer}
          setEmployeeList={setEmployeeList}
          allowCompanySelect
          onSuccess={refreshEmployeeList}
        />
      )}

      <Modal
        title="Importar Empleados"
        open={isImporterOpen}
        onCancel={() => setIsImporterOpen(false)}
        footer={null}
      >
        <EmployeeImporter
          onImportSuccess={handleImportSuccess}
          isModalOpen={isImporterOpen}
        />
      </Modal>
    </>
  );
};

export default AllEmployeeTable;
